<script setup lang="ts">
import { watch, nextTick, ref, computed, onUnmounted } from 'vue';
import { confirmState, settleConfirm } from '../game/confirm';
import { loadCheckoutTerms, type CheckoutTerms as Terms } from '../game/checkout-terms';
import CheckoutTerms from './CheckoutTerms.vue';
import LegalPanel from './LegalPanel.vue';
import { t } from '../i18n';

/** One dialog for the whole game — see confirm.ts. Mounted once in HeroScreen. */
const confirmBtn = ref<HTMLButtonElement | null>(null);

// ── Checkout (`opts.checkout`): consent gates the confirm button ──
const consent = ref(false);
const terms = ref<Terms | null>(null);
/** Slug of the legal document opened from the consent line, over the dialog. */
const legalDoc = ref<string | null>(null);
const blocked = computed(() => !!confirmState.opts?.checkout && !consent.value);

function confirm() {
  if (!blocked.value) settleConfirm(true);
}

function onKey(e: KeyboardEvent) {
  if (!confirmState.open) return;
  if (e.key !== 'Escape') return;
  e.preventDefault();
  // Esc backs out one layer: the document first, then the dialog
  if (legalDoc.value) legalDoc.value = null;
  else settleConfirm(false);
}
window.addEventListener('keydown', onKey);
onUnmounted(() => window.removeEventListener('keydown', onKey));

watch(() => confirmState.open, async (open) => {
  // every purchase asks afresh — a tick never carries over to the next dialog
  consent.value = false;
  legalDoc.value = null;
  if (!open) return;
  if (confirmState.opts?.checkout) void loadCheckoutTerms().then((v) => { terms.value = v; });
  await nextTick();
  if (!blocked.value) confirmBtn.value?.focus();
});
</script>

<template>
  <Transition name="cfh">
    <div
      v-if="confirmState.open && confirmState.opts"
      class="fixed inset-0 z-[400] grid place-items-center px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      style="background: rgba(4, 6, 14, 0.78); backdrop-filter: blur(3px)"
      @click.self="settleConfirm(false)"
    >
      <!-- THE WEIGHING — every spend in the game passes over this stone. It
           was a flat bordered card; now it's a granite tablet with the deal's
           subject floating over its brow and the terms carved as a ledger.
           `--acc` tints the stone's breath: gold for trade, blood for danger. -->
      <div
        class="cfh-card bound-frame relative"
        :class="{ 'cfh-danger': confirmState.opts.danger }"
        :style="{ '--acc': confirmState.opts.danger ? 'var(--color-error)' : 'var(--color-gold)' }"
      >
        <span class="cfh-cord" aria-hidden="true"></span>

        <div class="cfh-kicker">{{ confirmState.opts.kicker ?? t('confirmDialog.defaultKicker') }}</div>

        <img v-if="confirmState.opts.icon" :src="confirmState.opts.icon" alt="" class="cfh-art" />

        <h2 class="font-heading cfh-title" :class="confirmState.opts.danger ? 'cfh-title--blood' : 'gilt-text'">
          {{ confirmState.opts.title }}
        </h2>

        <div v-if="confirmState.opts.rows?.length" class="cfh-rows">
          <div v-for="(r, i) in confirmState.opts.rows" :key="i" class="cfh-row">
            <span class="cfh-row-label">{{ r.label }}</span>
            <span
              class="cfh-row-value"
              :class="{
                'text-gold': r.tone === 'gold' || !r.tone,
                'text-gold-bright': r.tone === 'bright',
                'text-error': r.tone === 'error',
                'text-success': r.tone === 'success',
                'cfh-row-value--dim': r.tone === 'dim',
              }"
            >
              <img v-if="r.icon" :src="r.icon" alt="" />
              {{ r.value }}
            </span>
          </div>
        </div>

        <p v-if="confirmState.opts.note" class="cfh-note">
          {{ confirmState.opts.note }}
        </p>

        <span class="cfh-rule" aria-hidden="true"></span>

        <CheckoutTerms
          v-if="confirmState.opts.checkout"
          v-model="consent"
          :terms="terms"
          @open-doc="legalDoc = $event"
        />

        <div class="flex items-center justify-center gap-2 mt-3.5">
          <button class="btn-forge btn-forge--carved" @click="settleConfirm(false)">
            {{ confirmState.opts.cancelLabel ?? t('confirmDialog.defaultCancel') }}
          </button>
          <button
            ref="confirmBtn"
            class="btn-forge"
            :class="confirmState.opts.danger ? 'btn-forge--blood' : 'btn-forge--rune'"
            :disabled="blocked"
            @click="confirm"
          >
            {{ confirmState.opts.confirmLabel ?? t('confirmDialog.defaultConfirm') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- a sibling of the dialog, not a child: the backdrop's filter would make it
       the containing block of this fixed panel. Lifted over the dialog (z-400). -->
  <div v-if="legalDoc" class="relative z-[410]">
    <LegalPanel
      :docs="terms?.docs ?? []"
      :initial-slug="legalDoc"
      @close="legalDoc = null"
    />
  </div>
</template>

<style scoped>
/* the stone: granite over a sunken body, the accent breathing up behind the
   brow where the subject floats — the same tablet grammar as the item modal */
.cfh-card {
  width: min(400px, 92vw);
  padding: 24px 22px 18px;
  text-align: center;
  background:
    radial-gradient(85% 36% at 50% 0%, color-mix(in srgb, var(--acc) 15%, transparent), transparent 72%),
    var(--tex-stone),
    linear-gradient(180deg, #131a26, #0c1119 58%, #0e141d);
  background-size: auto, 150px 150px, auto;
  background-blend-mode: normal, soft-light, normal;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.72), 0 0 26px color-mix(in srgb, var(--acc) 18%, transparent);
  animation: cfh-set 260ms cubic-bezier(0.2, 1.35, 0.35, 1) both;
}
.cfh-danger {
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.72),
    0 0 30px color-mix(in srgb, var(--color-error) 26%, transparent),
    inset 0 0 26px color-mix(in srgb, var(--color-error) 12%, transparent);
}
/* set down like a stone: drop in with a two-frame settle, not a fade */
@keyframes cfh-set {
  0% { opacity: 0; transform: translateY(18px) scale(0.985); }
  70% { opacity: 1; transform: translateY(-2px) scale(1.004); }
  100% { opacity: 1; transform: none; }
}

/* the accent cord chased into the brow — trade gold, danger blood */
.cfh-cord {
  position: absolute; top: 9px; left: 16px; right: 16px; height: 7px;
  background: var(--acc);
  -webkit-mask: var(--orn-cord-dark) repeat-x left center / auto 7px;
  mask: var(--orn-cord-dark) repeat-x left center / auto 7px;
  opacity: 0.9;
  pointer-events: none;
}

.cfh-kicker {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.09em;
  font-size: 10px; color: #9db0c6;
}

.cfh-art {
  width: 76px; height: 76px;
  margin: 8px auto 2px;
  object-fit: contain;
  filter: drop-shadow(0 10px 14px rgba(0, 0, 0, 0.75));
}

.cfh-title { font-size: clamp(19px, 5vw, 24px); line-height: 1.15; margin-top: 2px; }
.cfh-title--blood {
  color: #e08585;
  text-shadow: 0 0 16px color-mix(in srgb, var(--color-error) 45%, transparent), 0 2px 5px rgba(0, 0, 0, 0.9);
}

/* the terms, carved as a ledger: each line a shallow groove in the stone */
.cfh-rows { margin-top: 12px; }
.cfh-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 7px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.55);
  box-shadow: 0 1px 0 rgba(226, 236, 248, 0.05);
}
.cfh-row:last-child { border-bottom: 0; box-shadow: none; }
.cfh-row-label {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #9db0c6;
}
.cfh-row-value {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: var(--font-heading, serif);
  font-size: 16px; font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.8);
}
.cfh-row-value--dim { color: #b8c7d8; }
.cfh-row-value img { width: 16px; height: 16px; object-fit: contain; }

.cfh-note {
  font-family: var(--font-body, serif); font-style: italic;
  font-size: 12px; line-height: 1.45;
  color: #9db0c6;
  margin-top: 10px;
  max-width: 42ch; margin-inline: auto;
}

.cfh-rule {
  display: block; width: 170px; max-width: 65%; height: 9px; margin: 12px auto 0;
  background: var(--orn-divider-gold) no-repeat center / contain;
}
.cfh-danger .cfh-rule { filter: hue-rotate(-40deg) saturate(1.4); }

.cfh-enter-active, .cfh-leave-active { transition: opacity 160ms ease; }
.cfh-enter-from, .cfh-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .cfh-card { animation: none; }
  .cfh-enter-active, .cfh-leave-active { transition: none; }
}
</style>
